import { ChevronRightIcon } from "@heroicons/react/solid";
import axios from "axios";
import { useEffect, useState } from "react";
import { FaFeather, FaPlusSquare, FaSpinner } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import stringToColor from "string-to-color";
import api from "../api";
import { IFeature } from "../api/services/feature.service";
import { usePagination } from "../hooks/usePagination";
import Pagination from "./shared/Pagination";
import TextEditDialog from "./shared/TextEditDialog";

export default function FeatureListSection({
  pageSize,
}: {
  pageSize?: number;
}) {
  let navigate = useNavigate();

  const [features, setFeatures] = useState<IFeature[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [isFeatureAddDialogOpen, setIsFeatureAddDialogOpen] = useState(false);

  const { page, paginationProps, searchTerm, updatePagination, setPage } =
    usePagination();

  useEffect(() => {
    searchFeatures(searchTerm, page);
  }, [searchTerm, page]);

  async function searchFeatures(
    searchTerm: string | null,
    page: number | null
  ) {
    try {
      setLoading(true);
      const { data } = await api.feature.searchFeatures({
        term: searchTerm,
        page,
        size: pageSize || 10,
      });
      if (data.result) {
        const featurePageContent = data.result.content;
        setFeatures(featurePageContent);
        updatePagination(data);
        setLoading(false);
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response?.data) {
          toast.error(e.response?.data.errorMessage);
        }
      } else {
        console.log(e);
        toast.error(
          "Something really went wrong, you might want to contact support!"
        );
      }
    }
  }

  function FeatureRow({ feature }: { feature: IFeature }) {
    return (
      <div
        className="break-all py-4 px-4 cursor-pointer"
        onClick={() => navigate(`/feature/${feature.id}`)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0 mr-3 break-all">
              <FaFeather
                className="bg-white rounded-full h-10 w-10"
                color={stringToColor(feature.id)}
              />
            </div>
            <div className="break-all">
              <p className="text-sm font-medium text-red-600 truncate">
                {feature.name}
              </p>
            </div>
          </div>

          <div className="flex">
            {/* <div className="hidden md:flex mr-10">10</div> */}
            <ChevronRightIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    );
  }

  async function addFeature({ name }: { name: string }) {
    try {
      const { data } = await api.feature.addFeature(name);
      if (data.result) {
        navigate(`/feature/${data.result.id}`);
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response?.data) {
          toast.error(e.response?.data.errorMessage);
        }
      } else {
        console.log(e);
        toast.error(
          "Something really went wrong, you might want to contact support!"
        );
      }
    }
  }

  return (
    <div className="flex flex-col rounded-2xl shadow-xl bg-white grow">
      <TextEditDialog
        isOpen={isFeatureAddDialogOpen}
        title={"New Feature"}
        placeholder={"New Feature"}
        onCancel={() => {
          setIsFeatureAddDialogOpen(false);
        }}
        onSubmit={(newValue: string) => {
          addFeature({ name: newValue });
          setIsFeatureAddDialogOpen(false);
        }}
      />
      <div className="flex items-center p-4 w-full border-b justify-between">
        <div className="flex items-center">
          <div className="flex items-center mr-10 rounded-lg">
            <div className="">
              <FaFeather size={20} className="text-red-700" />
            </div>
            <span className="ml-2 font-medium leading-6 text-slate-700 text-lg flex ">
              Features
            </span>
          </div>
        </div>
        <div className="">
          <div
            className="text-indigo-600 cursor-pointer tour-1-step1"
            onClick={() => {
              setIsFeatureAddDialogOpen(true);
            }}
          >
            <FaPlusSquare size={24} />
          </div>
        </div>
      </div>
      {loading && (
        <div className="flex items-center justify-center p-20 text-slate-500">
          <FaSpinner size={30} className="animate-spin" />
        </div>
      )}
      {!loading && features.length > 0 && (
        <div className="flex flex-col divide-y divide-gray-200 w-full overflow-scroll overflow-x-hidden ">
          {features.map((feature, i) => (
            <FeatureRow key={i} feature={feature} />
          ))}
        </div>
      )}
      {!loading && features.length === 0 && (
        <div className="flex flex-col items-center justify-center p-8 text-slate-500">
          <div className="font-semibold text-slate-700 mb-2">
            No Features found
          </div>
          <div className="inline-flex items-center space-x-2">
            <div>Maybe try different search parameters</div>
          </div>
        </div>
      )}
      <Pagination {...paginationProps} goToPage={setPage} />
    </div>
  );
}
